import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Mail, ShoppingBag } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Success = () => {
  const [searchParams] = useSearchParams();
  const paymentMethod = searchParams.get("payment_method");
  const reference = searchParams.get("reference") || searchParams.get("trxref");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    // For Paystack, verify the payment
    if (paymentMethod === "paystack" && reference) {
      verifyPaystackPayment();
    } else {
      // If no reference but came from payment, show success
      setStatus("success");
    }
  }, [reference, attempts]);

  const verifyPaystackPayment = async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/paystack/verify/${reference}`);
      
      if (response.data.status === "success") {
        setStatus("success");
      } else if (response.data.status === "failed") {
        setStatus("error");
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      // If verification fails, still show success as payment might have gone through
      setStatus("success");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Success Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {status === "checking" && (
          <div className="text-center" data-testid="checking-status">
            <Loader2 className="h-16 w-16 text-sky-500 animate-spin mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Confirming Your Payment
            </h1>
            <p className="text-lg text-slate-600">
              Please wait while we verify your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center" data-testid="success-status">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Payment Successful!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for your purchase. Your formula package is ready!
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-sky-100 rounded-full p-3">
                  <Mail className="h-6 w-6 text-sky-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Check Your Email
                  </h3>
                  <p className="text-slate-600">
                    We've sent your download link and product details to your email address. Please check your inbox (and spam folder).
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-sky-100 rounded-full p-3">
                  <Download className="h-6 w-6 text-sky-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Instant Access
                  </h3>
                  <p className="text-slate-600">
                    You can download your formula package immediately using the link in your email.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Need Help?
              </h3>
              <p className="text-slate-600 mb-4">
                Our support team is available 24/7 to assist you with any questions about your formulations.
              </p>
              <a 
                href="https://wa.me/27730758123" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full" data-testid="whatsapp-support-btn">
                  Contact on WhatsApp
                </Button>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button variant="outline" className="rounded-full" data-testid="browse-more-btn">
                  Browse More Products
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full" data-testid="home-btn">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="error-status">
            <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Payment Verification Failed
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We encountered an error verifying your payment. Please contact support.
            </p>
            <a 
              href="https://wa.me/27730758123" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mb-4"
            >
              <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full">
                Contact on WhatsApp
              </Button>
            </a>
            <br />
            <Link to="/">
              <Button variant="outline" className="rounded-full mt-4">
                Back to Home
              </Button>
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div className="text-center" data-testid="timeout-status">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Payment Check Timed Out
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Please check your email for confirmation or contact support.
            </p>
            <Link to="/">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                Back to Home
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            © 2026 Formula Library. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Success;
